import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Unsubscriber } from '@core-lib/classes';
import { determineSize } from '@core-lib/functions';
import { IConfirmModalConfig } from '@core-lib/models';
import { DocumentGedService } from '@core-lib/modules/document-ged/services';
import { ConfirmationService } from '@core-lib/services/confirmation-service.service';

@Component({
    template: '',
    standalone: false
})
export class DocumentUploadBaseComponent extends Unsubscriber implements OnInit {
	@ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef;
	@Input() fileForm: FormGroup;
	@Input() isMultipleFileUpload: boolean;
	@Input() isOneFileOnlyAllowed: boolean = false;
	@Input() mimeTypesAllowed: string[] = [];
	@Input() filesTypesAllowed: string[] = [];
	@Input() showTrashButton: boolean = false;
	@Input() maxSize: number = 20000000; // (20Ko);

	isAsc: boolean = false;

	get fileArrayControls() {
		return (this.fileForm?.get('fileArray') as FormArray).controls;
	}

	constructor(
		private confirmationService: ConfirmationService,
		private documentGedService: DocumentGedService,
		private formBuilder: FormBuilder
	) {
		super();
	}

	ngOnInit(): void {}

	onFileDropped($event: any): void {
		if (this.fileArrayControls?.length === 1) {
			return;
		}
		this.addFile($event);
	}

	fileBrowseHandler(files: Array<File>): void {
		this.addFile(files);
	}

	private addFile(files: Array<File>): void {
		const validFiles = [];
		for (const item of files) {
			if (this.fileArrayControls?.find(x => x?.get('file')?.value?.name === item.name)) {
				this.showModal('Documents.Form.DuplicatedFile', { nom: item.name });
			} else if (!this.mimeTypesAllowed?.includes(item.type)) {
				this.showModal('Documents.Form.NotSuportedFileType', { nom: item.name });
			} else if (item.size > this.maxSize) {
				this.showModal('Documents.Form.TooLargeFile', { nom: item.name, maxSize: determineSize(this.maxSize) });
			} else {
				this.fileArrayControls.push(this.createFileControl(item));
			}
		}

		this.fileDropEl.nativeElement.value = '';
	}

	private createFileControl(file: File): FormGroup {
		return this.formBuilder.group({
			file: [file]
		});
	}

	removeFile(index: number): void {
		this.fileArrayControls.splice(index, 1);
	}

	showModal(textsKey: string, bodyParams?: any): void {
		const config: IConfirmModalConfig = {
			confirmBtnText: 'Actions.Ok',
			canClose: false,
			canConfirm: true,
			textsKey,
			bodyParams
		};
		this.confirmationService.openConfirmationModal(config);
	}

	sortTable(colName: string, isAsc: boolean): void {
		switch (colName) {
			case 'name':
				this.fileArrayControls.sort((a, b) => this.compare(a?.get('file')?.value?.name, b?.get('file')?.value?.name, isAsc));
				break;
			case 'size':
				this.fileArrayControls.sort((a, b) => this.compare(a?.get('file')?.value?.size, b?.get('file')?.value?.size, isAsc));
				break;
			default:
				null;
		}
		this.isAsc = !this.isAsc;
	}

	compare(a: number | string, b: number | string, isAsc: boolean): number {
		return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
	}

	formatBytes(bytes, decimals = 1): string {
		return this.documentGedService.formatBytes(bytes, decimals);
	}

	isOneFileOnly(): boolean {
		return this.isOneFileOnlyAllowed && this.fileArrayControls?.length === 1;
	}
}
